import * as React from 'react'
import { useCallback, useEffect } from 'react'
import { LocationDTO, ENTITY_TYPES } from '@sales-center/common'
import Box from '@mui/material/Box'
import useTheme from '@mui/material/styles/useTheme'
import Grid from '@mui/material/Grid'
import { Theme } from '@mui/material/styles'
import { LocationSelector } from './LocationSelector'
import { getPropertyRows, PropertyRow } from '../PropertyRows'
import { HighlightsCard } from './HighlightsCard'

type Props = {
  locations: LocationDTO[]
  locationId: number
  address?: string
  entityId?: number
  entityType?: ENTITY_TYPES
}

const getStyles = (theme: Theme) => ({
  root: {
    width: '100%',
    maxWidth: '848px',
    margin: `38px auto ${theme.spacing(2.8)}`,
    padding: theme.spacing(2),
    '@media (min-width: 1300px)': {
      padding: theme.spacing(0),
    },
  },

  heading: {
    maxWidth: '1299px',
    marginBottom: '16px',
  },
})

export function Details({
  locations,
  locationId,
  address,
  entityId,
  entityType,
}: Props) {
  const [selectedLocation, setSelectedLocation] =
    React.useState<LocationDTO | null>(null)
  const [propertyRows, setPropertyRows] = React.useState<PropertyRow[] | null>(
    null,
  )

  const theme = useTheme()
  const styles = getStyles(theme)

  const setLocation = useCallback(
    (loc: LocationDTO | null) => {
      if (loc) {
        setSelectedLocation(loc)
        setPropertyRows(
          getPropertyRows(
            loc.propertyDetails?.buildings?.[0] ?? null,
            'detail',
            'large',
          ),
        )
      }
    },
    [],
  )

  useEffect(() => {
    if (locations.length > 0) {
      setLocation(locations.find((l) => l.id === locationId) ?? null)
    }
  }, [locations, locationId, setLocation])

  return (
    <Box sx={styles.root}>
      <Grid item>
        <Box display="flex" sx={styles.heading}>
          {locations.length > 1 && (
            <Box
              sx={{
                width: 296,
                maxWidth: 296,
                backgroundColor: theme.palette.common.white,
              }}
            >
              <LocationSelector
                locations={locations}
                locationId={selectedLocation?.id}
                onChange={setLocation}
              />
            </Box>
          )}
        </Box>
      </Grid>

      <Grid item>
        {selectedLocation && (
          <HighlightsCard
            selectedLocation={selectedLocation}
            propertyRows={propertyRows}
            address={address}
            entityId={entityId}
            entityType={entityType}
          />
        )}
      </Grid>
    </Box>
  )
}
